import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiPlus, FiCalendar, FiClock, FiMapPin, FiVideo, FiUsers, FiEdit, FiTrash2, FiFilter, FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import meetingService from '../../services/meetingService';
import projectService from '../../services/projectService';
import CommonModal from '../../services/CommonModal';

const Meetings = () => {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
      const confirmDelete = () => {
    handleDelete(selectedMeeting.id);
    setShowDeleteModal(false);
    setSelectedMeeting(null);
  };
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    project_id: '',
    meeting_type: '',
    status: '',
    start_date: '',
    end_date: '',
  });

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  useEffect(() => {
    fetchMeetings();
    fetchProjects();
    fetchStats();
  }, [filters, pagination.page]);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const data = await meetingService.getMeetings({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });
      setMeetings(data.data || []);
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        pages: data.pages || 0,
      }));
    } catch (error) {
      console.error('Error fetching meetings:', error);
      toast.error('Failed to fetch meetings');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await projectService.getProjects({ limit: 1000 });
      setProjects(data.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await meetingService.getMeetingStats();
      setStats(data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleDelete = async (id) => {
    {
      try {
        await meetingService.deleteMeeting(id);
        toast.success('Meeting deleted successfully');
        fetchMeetings();
        fetchStats();
      } catch (error) {
        console.error('Error deleting meeting:', error);
        toast.error(error.response?.data?.error || 'Failed to delete meeting');
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      project_id: '',
      meeting_type: '',
      status: '',
      start_date: '',
      end_date: '',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getStatusColor = (status) => {
    const colors = {
      Scheduled: 'badge-primary',
      'In Progress': 'badge-warning',
      Completed: 'badge-success',
      Cancelled: 'badge-danger',
    };
    return colors[status] || 'badge-default';
  };

  const getMeetingTypeColor = (type) => {
    const colors = {
      Standup: 'badge-purple',
      Planning: 'badge-blue',
      Review: 'badge-success',
      Retrospective: 'badge-orange',
      Other: 'badge-gray',
    };
    return colors[type] || 'badge-default';
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate - startDate;
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins < 60) {
      return `${diffMins} min`;
    }
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Meetings</h1>
          <p className="text-gray-600 mt-1">Manage and schedule team meetings</p>
        </div>
        <Link
          to="/app/meetings/new"
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <FiPlus /> Schedule Meeting
        </Link>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">Total Meetings</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">Scheduled</div>
            <div className="text-2xl font-bold text-blue-600">{stats.by_status?.Scheduled || 0}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">In Progress</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.by_status?.['In Progress'] || 0}</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-sm text-gray-600">Completed</div>
            <div className="text-2xl font-bold text-green-600">{stats.by_status?.Completed || 0}</div>
          </div>
        </div>
      )}
                                 <CommonModal
        isOpen={showDeleteModal}
        title="Delete Meeting"
        message="Are you sure you want to delete this meeting?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedMeeting(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />
      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
          >
            <FiFilter /> {showFilters ? 'Hide' : 'Show'} Filters
          </button>
          {(filters.search || filters.project_id || filters.meeting_type || filters.status || filters.start_date || filters.end_date) && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 text-red-600 hover:text-red-700"
            >
              <FiX /> Clear Filters
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              type="text"
              name="search"
              placeholder="Search meetings..."
              value={filters.search}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            />
            <select
              name="project_id"
              value={filters.project_id}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Projects</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>{project.name}</option>
              ))}
            </select>
            <select
              name="meeting_type"
              value={filters.meeting_type}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Types</option>
              <option value="Standup">Standup</option>
              <option value="Planning">Planning</option>
              <option value="Review">Review</option>
              <option value="Retrospective">Retrospective</option>
              <option value="Other">Other</option>
            </select>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option value="">All Statuses</option>
              <option value="Scheduled">Scheduled</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
              placeholder="Start Date"
            />
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
              className="border border-gray-300 rounded-lg px-4 py-2"
              placeholder="End Date"
            />
          </div>
        )}
      </div>

      {/* Meetings List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading meetings...</div>
        ) : meetings.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No meetings found. Schedule your first meeting!
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {meetings.map((meeting) => (
              <div
                key={meeting.id}
                className="p-6 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/app/meetings/${meeting.id}`)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{meeting.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(meeting.status)}`}>
                        {meeting.status}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMeetingTypeColor(meeting.meeting_type)}`}>
                        {meeting.meeting_type}
                      </span>
                    </div>
                    
                    {meeting.description && (
                      <p className="text-gray-600 mb-3 line-clamp-2">{meeting.description}</p>
                    )}

                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <FiCalendar className="text-gray-400" />
                        {formatDateTime(meeting.start_time)}
                      </div>
                      <div className="flex items-center gap-1">
                        <FiClock className="text-gray-400" />
                        {formatDuration(meeting.start_time, meeting.end_time)}
                      </div>
                      {meeting.location && (
                        <div className="flex items-center gap-1">
                          <FiMapPin className="text-gray-400" />
                          {meeting.location}
                        </div>
                      )}
                      {meeting.meeting_url && (
                        <div className="flex items-center gap-1">
                          <FiVideo className="text-gray-400" />
                          Virtual Meeting
                        </div>
                      )}
                      {meeting.meeting_participants && (
                        <div className="flex items-center gap-1">
                          <FiUsers className="text-gray-400" />
                          {meeting.meeting_participants.length} participants
                        </div>
                      )}
                    </div>

                    {meeting.project && (
                      <div className="mt-2">
                        <span className="text-xs text-gray-500">Project: </span>
                        <span className="text-xs font-medium text-blue-600">{meeting.project.name}</span>
                      </div>
                    )}

                    {meeting.organizer && (
                      <div className="mt-1">
                        <span className="text-xs text-gray-500">Organizer: </span>
                        <span className="text-xs text-gray-700">
                          {meeting.organizer.first_name} {meeting.organizer.last_name}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/app/meetings/${meeting.id}/edit`);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit"
                    >
                      <FiEdit />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedMeeting(meeting);
                        setShowDeleteModal(true);
                      }}
                      className="p-2 text-red-600 hover:bg-red-200 rounded"
                      title="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} meetings
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Meetings;


